// File-input wrapped helper that uploads a logo to the `branding` bucket
// and returns the public URL. The bucket is tenant-scoped via RLS — see
// supabase-adapter/storage-service.ts which prepends the tenant_id prefix.

import React, { useRef, useState } from 'react';
import { Button, Input } from '@erp/ui';
import { useStorageService } from '@/lib/service-context';
import { Upload, X } from '@/components/icons';
import { useTranslation } from 'react-i18next';

interface LogoUploaderProps {
  /** Current value to display. Receives null when the user clears it. */
  value: string | null;
  onChange: (next: string | null) => void;
  /** Used to namespace the storage path (logo / logo-dark / splash). */
  variant: 'logo' | 'logo-dark' | 'splash';
  /** Visible label above the field. */
  label: string;
  hint?: string;
}

export function LogoUploader({ value, onChange, variant, label, hint }: LogoUploaderProps) {
  const { t } = useTranslation();
  const storage = useStorageService();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      setError(t('appBuilder.logo.tooLarge', { defaultValue: 'Image trop lourde (max 4 Mo)' }));
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError(t('appBuilder.logo.notImage', { defaultValue: 'Format non supporté (PNG/JPG/SVG/WebP)' }));
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'png';
      const path = `app-builder/${variant}-${Date.now()}.${ext}`;
      const stored = await storage.upload('branding', path, file);
      const url = stored.startsWith('http') ? stored : storage.getPublicUrl('branding', stored);
      onChange(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
      // Reset so the user can re-upload the same file if needed.
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>

      {value ? (
        <div className="flex items-center gap-3 rounded-lg border border-input p-2">
          <img
            src={value}
            alt={label}
            className="h-12 w-12 rounded object-contain bg-muted"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value || null)}
            className="flex-1 font-mono text-xs"
          />
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={() => onChange(null)}
            aria-label={t('common.remove', { defaultValue: 'Supprimer' })}
          >
            <X size={16} />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Input
            placeholder="https://… (collez une URL ou téléversez)"
            value=""
            onChange={(e) => onChange(e.target.value || null)}
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            <Upload size={16} />
            {uploading
              ? t('common.uploading', { defaultValue: 'Téléversement…' })
              : t('common.upload', { defaultValue: 'Téléverser' })}
          </Button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
        onChange={handleFile}
        className="hidden"
      />

      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
